import { getMessenger, getIsReady } from '../hooks/useChatInitialization';
import { getParamFromUrl } from './tools'
import Login from "../pages/login";
import { v1 as uuid } from 'uuid';

/**
 * 创建安全的 fetch 包装器，根据 serverHost 参数动态替换 API 地址
 * @param baseFetcher 基础 fetch 函数，默认使用 window.fetch
 * @returns 包装后的 fetch 函数
 */
export const createSafeFetcher = (baseFetcher: typeof fetch = window.fetch.bind(window)) => {
  const serverHost = getParamFromUrl('serverHost');
  return (...args: Parameters<typeof fetch>): Promise<Response> => {
    if (typeof args[0] === 'string' && serverHost) {
      args[0] = args[0]
        .replace('https://hgentx-mentor.ekuaibao.com', serverHost);
    }
    return baseFetcher.apply(window, args);
  };
};

export const makeMessageKey = (key: string) => {
  const chatId = getParamFromUrl('chatId')
  return `${chatId}${key}`
}

/**
 * 对话框回调注册表
 * 用于存储对话框确认回调函数，解决 postMessage 无法序列化函数的问题
 */
const dialogCallbacks = new Map<string, () => void>();

/**
 * 消息回复回调注册表
 * 用于存储等待父应用回复的回调函数
 */
const responseCallbacks = new Map<string, {
  resolve: (data: any) => void;
  reject: (error: any) => void;
  timeout?: NodeJS.Timeout;
}>();

/**
 * 注册对话框回调
 * @param callback 回调函数
 * @returns 回调ID
 */
export const registerDialogCallback = (callback: () => void): string => {
  const callbackId = `dialog_callback_${uuid()}`;
  dialogCallbacks.set(callbackId, callback);

  // 设置超时清理（5分钟后自动清理，防止内存泄漏）
  setTimeout(() => {
    dialogCallbacks.delete(callbackId);
  }, 5 * 60 * 1000);

  return callbackId;
};

/**
 * 执行对话框回调
 * @param callbackId 回调ID
 */
export const executeDialogCallback = (callbackId: string): void => {
  const callback = dialogCallbacks.get(callbackId);
  if (callback) {
    try {
      callback();
    } catch (error) {
      console.error('执行对话框回调失败:', error);
    } finally {
      // 执行后立即清理
      dialogCallbacks.delete(callbackId);
    }
  } else {
    console.warn(`未找到回调ID: ${callbackId}`);
  }
};

/**
 * 清理对话框回调
 * @param callbackId 回调ID
 */
export const clearDialogCallback = (callbackId: string): void => {
  dialogCallbacks.delete(callbackId);
};

/**
 * 注册消息回复回调
 * @param resolve Promise 的 resolve 函数
 * @param reject Promise 的 reject 函数
 * @param timeoutMs 超时时间（毫秒），默认30秒
 * @returns 消息ID
 */
export const registerResponseCallback = (
  resolve: (data: any) => void,
  reject: (error: any) => void,
  timeoutMs: number = 30000
): string => {
  const messageId = `msg_${uuid()}`;

  // 设置超时
  const timeout = setTimeout(() => {
    const callback = responseCallbacks.get(messageId);
    if (callback) {
      responseCallbacks.delete(messageId);
      reject(new Error('等待父应用回复超时'));
    }
  }, timeoutMs);

  responseCallbacks.set(messageId, {
    resolve,
    reject,
    timeout
  });

  return messageId;
};

/**
 * 处理父应用回复
 * @param messageId 消息ID
 * @param data 回复数据
 * @param isError 是否为错误回复
 */
export const handleParentResponse = (messageId: string, data: any, isError: boolean = false): void => {
  const callback = responseCallbacks.get(messageId);
  if (callback) {
    // 清理超时定时器
    if (callback.timeout) {
      clearTimeout(callback.timeout);
    }

    // 删除回调
    responseCallbacks.delete(messageId);

    // 执行回调
    if (isError) {
      callback.reject(data);
    } else {
      callback.resolve(data);
    }
  } else {
    console.warn(`未找到消息回调ID: ${messageId}`);
  }
};

/**
 * 清理消息回复回调
 * @param messageId 消息ID
 */
export const clearResponseCallback = (messageId: string): void => {
  const callback = responseCallbacks.get(messageId);
  if (callback) {
    if (callback.timeout) {
      clearTimeout(callback.timeout);
    }
    responseCallbacks.delete(messageId);
  }
};

/**
 * Postmate 工具函数
 */

/**
 * 发送消息到父页面
 * @param eventName 事件名称
 * @param data 数据
 * @returns Promise<boolean> 发送是否成功
 */
export const sendToParent = async (eventName: string, data: any): Promise<boolean> => {
  const messenger = getMessenger();
  const isReady = getIsReady();

  console.log('🔍 发送消息调试信息:', {
    eventName,
    hasMessenger: !!messenger,
    isReady,
    isEmbedded: window.self !== window.top,
    dataSize: JSON.stringify(data).length
  });

  if (!messenger) {
    console.warn('❌ Postmate messenger 未初始化，无法发送消息');
    console.warn('可能的原因:');
    console.warn('1. 不在 iframe 环境中');
    console.warn('2. Postmate handshake 失败');
    console.warn('3. 父页面未正确初始化 Postmate');
    return false;
  }

  if (!isReady) {
    console.warn('❌ Postmate 连接未就绪，无法发送消息');
    return false;
  }

  try {
    const payload = {
      type: eventName,
      data,
      timestamp: Date.now()
    };

    messenger.emit(eventName, payload);

    console.log(`✅ 消息已发送到父页面: ${eventName}`, payload);
    return true;
  } catch (error) {
    console.error(`❌ 发送消息失败: ${eventName}`, error);
    return false;
  }
};

/**
 * 发送 AI 填单结果到父页面
 * @param formData 填单数据
 * @returns Promise<boolean> 发送是否成功
 */
export const sendFillFormResult = async (formData: any): Promise<boolean> => {
  return sendToParent(makeMessageKey('ai:fillFormResult'), formData);
};

/**
 * 发送状态更新到父页面
 * @param status 状态信息
 * @returns Promise<boolean> 发送是否成功
 */
export const sendStatusUpdate = async (status: any): Promise<boolean> => {
  return sendToParent(makeMessageKey('status:update'), status);
};

/**
 * 发送错误信息到父页面
 * @param error 错误信息
 * @returns Promise<boolean> 发送是否成功
 */
export const sendError = async (error: any): Promise<boolean> => {
  return sendToParent(makeMessageKey('error:occurred'), {
    message: error.message || error,
    stack: error.stack,
    timestamp: Date.now()
  });
};

/**
 * 检查是否可以与父页面通信
 * @returns boolean
 */
export const canCommunicateWithParent = (): boolean => {
  return getMessenger() !== null && getIsReady();
};

/**
 * 通用的消息发送器，支持回调
 * @param eventName 事件名称
 * @param data 数据
 * @param onSuccess 成功回调
 * @param onError 错误回调
 */
export const sendMessage = (
  eventName: string,
  data: any,
  onSuccess?: () => void,
  onError?: (error: any) => void
) => {
  sendToParent(eventName, data)
    .then(success => {
      if (success && onSuccess) {
        onSuccess();
      } else if (!success && onError) {
        onError(new Error('消息发送失败'));
      }
    })
    .catch(error => {
      if (onError) {
        onError(error);
      }
    });
};

export const sendStartNewChat = async () => {
  sendToParent(makeMessageKey('startNewChat'), {});
};

export const sendRefreshToken = async () => {
  sendToParent(makeMessageKey('refreshToken'), {});
};

export const sendPreviewAttachmentOrInvoice = async (data: any) => {
  sendToParent(makeMessageKey('ai:previewAttachmentOrInvoice'), data);
};

export const sendPreviewFile = async (data: any) => {
  sendToParent(makeMessageKey('previewFile'), data);
};

/**
 * 发送AI反馈点赞到父页面
 * @param data 反馈数据
 * @returns Promise<boolean> 发送是否成功
 */
export const sendAIFeedbackLike = async (data: any): Promise<boolean> => {
  return sendToParent(makeMessageKey('ai:feedback:like'), data);
};

/**
 * 发送AI反馈点踩到父页面
 * @param data 反馈数据
 * @returns Promise<boolean> 发送是否成功
 */
export const sendAIFeedbackDislike = async (data: any): Promise<boolean> => {
  return sendToParent(makeMessageKey('ai:feedback:dislike'), data);
};

/**
 * 打开关联申请的弹框
 * @param data ai 数据
 * @returns Promise<boolean> 发送是否成功
 */
export const openBillStackerModal = async (data: any): Promise<boolean> => {
  return sendToParent(makeMessageKey('ai:add:expense:link'), data);
};

/**
 * 打开选择单据模板的弹框
 * @param data ai 数据
 * @returns Promise<boolean> 发送是否成功
 */
export const openFlowTemplateModal = async (data: any): Promise<boolean> => {
  return sendToParent(makeMessageKey('ai:select:flow:template'), data);
};

/**
 * 打开费用类型选择弹窗
 * @param data 传递的数据
 * @returns Promise<boolean>
 */
export const openFeeTypeModal = async (data: any): Promise<boolean> => {
  return sendToParent(makeMessageKey('ai:select:fee:type'), data);
};


/**
 * 打开移动端 toast
 * @param data 传递的数据
 * @returns Promise<boolean>
 */
export const showToast = async (data: any): Promise<boolean> => {
  return sendToParent(makeMessageKey('showToast'), data);
};

/**
 * 打开移动端 Dialog
 * @param data 传递的数据
 * @returns Promise<boolean>
 */
export const showDialog = async (data: any): Promise<boolean> => {
  // 如果 data 中包含 onConfirm 函数，需要特殊处理
  let dialogData = { ...data };

  if (typeof data.onConfirm === 'function') {
    // 注册回调函数并替换为回调ID
    const callbackId = registerDialogCallback(data.onConfirm);
    dialogData = {
      ...dialogData,
      onConfirm: undefined, // 移除函数
      callbackId, // 添加回调ID
    };
  }

  return sendToParent(makeMessageKey('showDialog'), dialogData);
};


/**
 * 发送消息到父页面并等待回复
 * @param eventName 事件名称
 * @param data 数据
 * @param timeoutMs 超时时间（毫秒），默认30秒
 * @returns Promise<any> 父应用的回复数据
 */
export const sendToParentAndWaitResponse = async (
  eventName: string,
  data: any,
  timeoutMs: number = 30000
): Promise<any> => {
  const messenger = getMessenger();
  const isReady = getIsReady();

  console.log('🔍 发送消息并等待回复调试信息:', {
    eventName,
    hasMessenger: !!messenger,
    isReady,
    isEmbedded: window.self !== window.top,
    dataSize: JSON.stringify(data).length
  });

  if (!messenger) {
    throw new Error('Postmate messenger 未初始化，无法发送消息');
  }

  if (!isReady) {
    throw new Error('Postmate 连接未就绪，无法发送消息');
  }

  return new Promise((resolve, reject) => {
    // 注册回复回调
    const messageId = registerResponseCallback(resolve, reject, timeoutMs);

    try {
      const payload = {
        type: eventName,
        data,
        messageId, // 添加消息ID，用于父应用回复时匹配
        timestamp: Date.now()
      };

      messenger.emit(eventName, payload);
      console.log(`✅ 消息已发送到父页面并等待回复: ${eventName}`, payload);
    } catch (error) {
      // 如果发送失败，清理回调
      clearResponseCallback(messageId);
      console.error(`❌ 发送消息失败: ${eventName}`, error);
      reject(error);
    }
  });
};

/**
 * 打开各种上传发票的弹框并等待回复
 * @param data 传递的数据
 * @param timeoutMs 超时时间（毫秒），默认1h
 * @returns Promise<any> 父应用的回复数据
 */
export const sendSelectInvoice = async (data: any, timeoutMs: number = 3600000): Promise<any> => {
  return sendToParentAndWaitResponse(makeMessageKey('ai:upload:invoice'), data, timeoutMs);
}

/**
 * 删除消费记录
 * @param data 传递的数据
 * @returns Promise<any> 父应用的回复数据
 */
export const deleteConsumptionById = async (data: any, timeoutMs: number = 10000): Promise<any> => {
  return sendToParentAndWaitResponse(makeMessageKey('ai:delete:consumption:by:id'), data, timeoutMs);
}

/**
 * 更新聊天历史中的消息内容
 * @param chatId 聊天会话ID
 * @param msgId 消息ID
 * @param msgBody 更新后的消息体
 * @returns Promise<any> 服务端响应
 */
export const updateChatHistory = async (
  chatId: string,
  msgId: string,
  msgBody: any
): Promise<any> => {
  const safeFetcher = createSafeFetcher(Login.fetcher);
  const response = await safeFetcher('https://hgentx-mentor.ekuaibao.com/api/v1/chat/history/update', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      chatId,
      msgId,
      msgBody
    })
  });

  if (!response.ok) {
    throw new Error(`更新聊天历史失败: ${response.status} ${response.statusText}`);
  }

  return response.json();
}